<script setup lang="ts">
import Modal from '@/components/shared/Dialog.vue';
import Timeline from 'primevue/timeline';
import Tag from 'primevue/tag';
import TimelineMarker from '../timeline/TimelineMarker.vue';
import TimelineConnector from '../timeline/TimelineConnector.vue';
import {
  CAPACITY_TYPES,
  CAPACITY_STATUSES,
  CAPACITY_APPROVAL_VALIDITY_DAYS,
} from '@/data/capacity';
import type { TagSeverity } from '@/types/ui.types';

interface Props {
  visible: boolean;
  titleTag: string | undefined;
  capacityType: string;
}
const { visible, titleTag, capacityType } = defineProps<Props>();

// methods
const getTagSeverity = (capacityStatus: string): TagSeverity => {
  switch (capacityStatus) {
    case CAPACITY_STATUSES.approved:
      return 'success';
    case CAPACITY_STATUSES.preliminary:
      return 'info';
    case CAPACITY_STATUSES.self_reported:
      return 'warning';
    default:
      return 'info';
  }
};

// data
const approvedDays =
  capacityType === CAPACITY_TYPES.rolling_funding
    ? CAPACITY_APPROVAL_VALIDITY_DAYS.rolling_funding
    : CAPACITY_APPROVAL_VALIDITY_DAYS.fixed_funding;

const fundingCapacityStatuses = [
  {
    title: CAPACITY_STATUSES.self_reported,
    body: 'Estimated Funding Capacity based on your self-reported revenue during initial onboarding.',
    severity: 'warning',
  },
  {
    title: CAPACITY_STATUSES.preliminary,
    body: 'Estimated Funding Capacity based on your connected revenue accounts. Approvals are subject to Diligence review.',
    severity: 'info',
  },
  {
    title: CAPACITY_STATUSES.approved,
    body: `Approved Funding Capacity by our diligence team. This amount is available for ${approvedDays} days from the approval date, unless otherwise stated on the dashboard, and may be used to fund invoices and receipts.`,
    severity: 'success',
  },
];

const emit = defineEmits(['hide']);
</script>

<template>
  <Modal
    :visible="visible"
    :modal="true"
    :closable="true"
    :draggable="false"
    @update:visible="(value: boolean) => !value && emit('hide')"
  >
    <template #header>
      <div class="modal-header-container">
        <div class="modal-header">Funding Capacity Statuses</div>
        <div class="modal-description">
          Here are the different Funding Capacity statuses and how they are
          determined.
        </div>
      </div>
    </template>

    <div class="modal-body">
      <div class="timeline-container">
        <Timeline :value="fundingCapacityStatuses" class="timeline">
          <template #content="slotProps">
            <div class="timeline-step-container">
              <div>
                <Tag
                  :value="slotProps.item.title"
                  :severity="getTagSeverity(slotProps.item.title)"
                  class="title-tag"
                />
              </div>
              <span class="step-description">{{ slotProps.item.body }}</span>
            </div>
          </template>

          <template #marker="slotProps">
            <TimelineMarker
              :color="slotProps.item.title === titleTag ? '#a36bff' : undefined"
            />
          </template>

          <template #connector>
            <TimelineConnector />
          </template>
        </Timeline>
      </div>
    </div>
  </Modal>
</template>

<style lang="less" scoped>
.modal-header-container {
  text-align: left;
  margin-top: 10px;
  font-family: @regular-font;
  color: black;

  @media (min-width: 680px) {
    margin-left: -80px;
  }

  .modal-header {
    font-size: 22px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  .modal-description {
    font-size: 14px;
    font-weight: 300;
  }
}

.modal-body {
  max-width: 600px;
  padding: 10px 10px 5px 10px;
}

.timeline-container {
  :deep(.p-timeline-event) {
    display: flex;
    min-height: 0;
  }

  :deep(.p-timeline-event-opposite) {
    display: none;
  }

  :deep(.p-timeline-left .p-timeline-event-content) {
    margin-bottom: 40px;
  }

  :deep(
    .p-timeline-left .p-timeline-event:last-child .p-timeline-event-content
  ) {
    margin-bottom: 0;
  }
}

.timeline-step-container {
  font-family: @regular-font;
  color: black;
  margin-top: -8px;

  .title-tag {
    font-size: 15px;
    font-weight: 400;
    margin-bottom: 4px;
    padding: 4px 8px;
  }

  .step-description {
    line-height: 20px;
    font-size: 14px;
    font-weight: 300;
    display: block;
  }
}
</style>
